import { Country, CountryDescriptor } from '@app/enums/country';
import { MotivationDescriptor } from '@app/enums/motivation';
import { BusinessSectorDescriptor } from '@app/enums/business-sector';
import { NumberOfEmployeesDescriptor } from '@app/enums/number-of-employees';
import { EntitySizeDescriptor, ESAdditionalInformationDescriptor } from '@app/enums/entity-size';
import { ActorTypeDescriptor } from '@app/enums/actor-type';
import { IncidentModel } from '@app/models/incident.model';
import { formatAsDateOnlyString } from '@app/utils/datetime/datetime.utils';
import { formatDecimalString } from '@app/utils/number/number.utils';
import { IncidentModelFieldName, RowType } from './models/merge-row.model';
import { MergeColumnValueType } from './models/merge-column.model';

const entitySizeThresholdDescriptor = ESAdditionalInformationDescriptor as Record<string, string>;

export type RowDefinition = {
  displayName: string;
  field?: IncidentModelFieldName;
  type: RowType;
  valueSelector: (incident: IncidentModel) => MergeColumnValueType | undefined;
  description?: string | null;
};

export const detailsRowDefinitions: RowDefinition[] = [
  {
    displayName: 'Title',
    field: 'title',
    type: 'text-expandable',
    valueSelector: (incident) => incident.title,
  },
  {
    displayName: 'Summary',
    field: 'summary',
    type: 'text-expandable',
    valueSelector: (incident) => incident.summary,
  },
  {
    displayName: 'Time span of attack',
    field: 'custom.timeSpanOfAttack',
    type: 'text',
    valueSelector: (incident) => getTimeSpanLabel(incident),
  },
  {
    displayName: 'Country of victim',
    field: 'entity.countryCode',
    type: 'country',
    valueSelector: (incident) => mapCountryValue(incident.entity?.countryCode),
  },
  {
    displayName: 'Motivation of threat actor',
    field: 'motivation',
    type: 'text',
    valueSelector: (incident) => (MotivationDescriptor as Record<string, string>)[incident.motivation || 'Unknown'],
  },
];

export const entityRowDefinitions: RowDefinition[] = [
  {
    displayName: 'Entity name',
    field: 'custom.entity',
    type: 'text',
    valueSelector: (incident) => incident.entity?.name,
  },
  {
    displayName: 'Business / Professional Services',
    type: 'text',
    valueSelector: (incident) =>
      (BusinessSectorDescriptor as Record<string, string>)[incident.entity?.businessSector || ''],
  },
  {
    displayName: 'Home country',
    type: 'country',
    valueSelector: (incident) => mapCountryValue(incident.entity?.countryCode),
  },
  {
    displayName: 'Website',
    type: 'website',
    valueSelector: (incident) => normalizeWebsite(incident.entity?.website),
  },
  {
    displayName: 'Size',
    type: 'text',
    valueSelector: (incident) => getEntitySizeLabel(incident),
  },
  {
    displayName: 'Entity type',
    type: 'text',
    valueSelector: (incident) => incident.entity?.entityType ?? undefined,
  },
  {
    displayName: 'Revenue',
    type: 'text',
    valueSelector: (incident) => getEntityRevenueLabel(incident),
  },
  {
    displayName: 'Number of employees',
    type: 'text',
    valueSelector: (incident) =>
      (NumberOfEmployeesDescriptor as Record<string, string>)[incident.entity?.numberOfEmployees || ''],
  },
  {
    displayName: 'Headquarter address',
    type: 'text',
    valueSelector: (incident) => incident.entity?.headquarterAddress,
  },
  {
    displayName: 'Dun & Bradstreet',
    type: 'text',
    valueSelector: (incident) => incident.entity?.dnbId,
  },
];

export const threatActorRowDefinitions: RowDefinition[] = [
  {
    displayName: 'Name',
    field: 'custom.actor',
    type: 'text',
    valueSelector: (incident) => incident.actor?.name,
  },
  {
    displayName: 'Actor Type',
    type: 'text',
    valueSelector: (incident) => (ActorTypeDescriptor as Record<string, string>)[incident.actor?.type || ''],
  },
  {
    displayName: 'Profile Link',
    type: 'website',
    valueSelector: (incident) => normalizeWebsite(incident.actor?.profileLink),
  },
];

export function getTimeSpanLabel(incident: IncidentModel): string | undefined {
  const start = formatAsDateOnlyString(incident.startedAt);
  const end = formatAsDateOnlyString(incident.endedAt ?? null);

  if (!start) {
    return undefined;
  }

  return end ? `${start} - ${end}` : start;
}

export function mapCountryValue(countryCode: keyof typeof Country | undefined): {
  countryCode: Country;
  countryName: string;
} {
  const normalizedCountryCode = (countryCode ?? Country.Unknown) as Country;

  return {
    countryCode: normalizedCountryCode,
    countryName: (CountryDescriptor as Record<string, string>)[normalizedCountryCode] || 'Unknown',
  };
}

export function getEntitySizeLabel(incident: IncidentModel): string | undefined {
  const entitySize = incident.entity?.entitySize;
  const label = (EntitySizeDescriptor as Record<string, string>)[entitySize || ''];
  const threshold = entitySizeThresholdDescriptor[entitySize || ''];

  if (!label) {
    return undefined;
  }

  return threshold ? `${label} - ${threshold}` : label;
}

export function getEntityRevenueLabel(incident: IncidentModel): string | undefined {
  const revenue = incident.entity?.revenue;
  if (revenue == null) {
    return undefined;
  }

  return `~ ${formatDecimalString(revenue)} USD`;
}

export function normalizeWebsite(url: string | null | undefined): string | undefined {
  if (!url) {
    return undefined;
  }

  return /^https?:\/\//i.test(url) ? url : `https://${url}`;
}

const allRowDefinitions: RowDefinition[] = [
  ...detailsRowDefinitions,
  ...entityRowDefinitions,
  ...threatActorRowDefinitions,
];

export function findRowDefinitionByField(field?: IncidentModelFieldName): RowDefinition | undefined {
  return field ? allRowDefinitions.find((definition) => definition.field === field) : undefined;
}
